import React from 'react';
import { Col, Form, Input, Row, Typography, message, Select } from 'antd';
import { SYSTEM_PROMPTS } from './SystemPrompts.constants';
import { useTranslation } from 'react-i18next';
import BeautifyControl from '../../../Shared/BeautifyControl/BeautifyControl';
import { beautifySystemInstruction } from '../../AgentServices';
import { HighlightOutlined } from '@ant-design/icons';
import { ICON_SIZE } from '../../../Shared/Styles/iconButton';

const { Text } = Typography;

const AgentNameInput = ({ nameRef, instructionsRef }) => {
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language ? i18n.language.split('-')[0] : 'fr';
    const localizedPrompts = SYSTEM_PROMPTS[currentLang] || SYSTEM_PROMPTS['fr'] || [];
    const form = Form.useFormInstance();

    const runBeautifyInstruction = React.useCallback(
        async (text) => {
            try {
                const data = await beautifySystemInstruction({
                    draft_system_instruction: text,
                });

                // Support both nested `data` and flat responses
                const root = data?.data || data || {};

                const rawWarnings = root?.warnings || [];
                const warnings = Array.isArray(rawWarnings) ? rawWarnings : [];

                let rawSuggestions = root?.suggestions || data?.suggestions || [];
                if (!Array.isArray(rawSuggestions)) {
                    rawSuggestions = [];
                }

                // Normalize suggestions to plain strings
                let suggestionsList = rawSuggestions
                    .map((s) => {
                        if (typeof s === 'string') return s;
                        return '';
                    })
                    .filter(Boolean);

                // Some backends might return a single suggestion field
                if (!suggestionsList.length && typeof root.system_instruction === 'string') {
                    suggestionsList = [root.system_instruction];
                }

                if (!suggestionsList.length) {
                    message.info(t('no_suggestions_available'));
                    return { suggestions: [] };
                }

                const coinsAmountRaw =
                    typeof root.coins_amount === 'number'
                        ? root.coins_amount
                        : typeof data?.coins_amount === 'number'
                        ? data.coins_amount
                        : null;

                const analysisText = root.analysis || data?.analysis || '';

                return {
                    suggestions: suggestionsList,
                    coinsAmount:
                        typeof coinsAmountRaw === 'number'
                            ? Math.abs(coinsAmountRaw)
                            : null,
                    analysis: analysisText,
                    warnings,
                };
            } catch (error) {
                message.error(
                    error?.response?.data?.message || t('something_went_wrong')
                );
                return { suggestions: [] };
            }
        },
        [t]
    );

    return (
        <>
            <Col xs={24} md={24}>
                <Form.Item
                    name="name"
                    label={t('agent_name')}
                    style={{ marginBottom: 4 }}
                    rules={[{ required: true, message: t('please_input_the_agent_name') }]}
                >
                    <Input placeholder={t('enter_agent_name')} data-tour="agent-name" ref={nameRef} />
                </Form.Item>
            </Col>
            
            <Col xs={24}>
                <div style={{ marginTop: '16px' }}>
                    <Text style={{ color: '#1677ff', fontSize: '16px', fontWeight: 'bold', marginBottom: '12px', display: 'block' }}>{t('agent_instructions')}</Text>
                    <Row gutter={[16, 0]} align="middle">
                        <Col xs={24}>
                            <div style={{ marginBottom: '12px' }}>
                                <Select
                                    showSearch
                                    allowClear
                                    defaultValue={form.getFieldValue('system_instruction')}
                                    placeholder={t('select_a_template', { defaultValue: 'Sélectionner un modèle...' })}
                                    style={{ width: '100%' }}
                                    onChange={(value) => {
                                        if (value) {
                                            form.setFieldsValue({ system_instruction: value });
                                            // Trigger validation to ensure form state updates
                                            form.validateFields(['system_instruction']);
                                        }
                                    }}
                                    optionFilterProp="children"
                                >
                                    <Select.Option key="default_template" value={t('default_system_instruction')}>
                                        {t('default_template_name')}
                                    </Select.Option>
                                    {localizedPrompts.map((category) => (
                                        <Select.OptGroup key={category.name} label={category.name}>
                                            {category.subcategories.map((sub) => (
                                                <Select.Option key={sub.name} value={sub.prompt}>
                                                    {sub.name}
                                                </Select.Option>
                                            ))}
                                        </Select.OptGroup>
                                    ))}
                                </Select>
                            </div>
                            <Form.Item 
                                style={{ marginBottom: 0 }}
                                name="system_instruction"
                                label={t('agent_instructions_label')}
                            >
                                <Input.TextArea 
                                    rows={4} 
                                    data-tour="agent-instructions"
                                    ref={instructionsRef}
                                    dir="auto"
                                    style={{ unicodeBidi: 'plaintext' }}
                                />
                            </Form.Item>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prev, current) =>
                                    prev.system_instruction !==
                                    current.system_instruction
                                }
                            >
                                {({ getFieldValue }) => {
                                    const value =
                                        getFieldValue('system_instruction') || '';
                                    const disabled = !value.trim();

                                    return (
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                                marginTop: 8,
                                            }}
                                        >
                                            <BeautifyControl
                                                value={value}
                                                onChange={(next) =>
                                                    form.setFieldsValue({
                                                        system_instruction: next,
                                                    })
                                                }
                                                runBeautify={
                                                    runBeautifyInstruction
                                                }
                                                beautifyLabel={t('beautify')}
                                                beautifyIcon={<HighlightOutlined style={{ fontSize: ICON_SIZE }} />}
                                                revertLabel={t('revert')}
                                                disabled={disabled}
                                                buttonStyle={{ color: '#7F56D9' }}
                                            />
                                        </div>
                                    );
                                }}
                            </Form.Item>
                        </Col>
                    </Row>
                </div>
            </Col>
        </>
    );
};

export default AgentNameInput;
